import { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getTomorrow } from "../../../../pages/SchoolDashboard/Driver/AddSchedule";
import {
  useCreateBusingSubscriptionMutation,
  useUpdateBusingSubscriptionMutation,
  useUploadStudentListMutation,
} from "../../../../store/api/school/ReservationSlice";
import { parseLocalDate } from "../../../../utils";
import {
  formatDateToYMD,
  toMinutes,
} from "../../../../utils/dateAndTimeConversion";
import CustomDatePicker from "../../../CustomDatePicker";

const initialState = {
  subscription_type: "morning",
  start_date: "",
  end_date: "",
  start_time: "",
  end_time: "",
  return_start_time: "",
  return_end_time: "",
  reserved_seats: "",
  vehicle_id: "",
  notes: "",
};

const BusingSubscriptions = ({
  onClose,
  user = null,
  selectedVehicle = null,
  selectedReservation = null,
}) => {
  const tomorrow = getTomorrow();
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [studentFile, setStudentFile] = useState(null);
  const [csvRowCount, setCsvRowCount] = useState(null); // number of data rows in CSV
  const fileInputRef = useRef(null);

  // Count data rows in CSV (exclude header row)
  const countCsvRows = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const lines = e.target.result
          .split("\n")
          .map((l) => l.trim())
          .filter((l) => l.length > 0);
        resolve(Math.max(0, lines.length - 1)); // subtract header
      };
      reader.readAsText(file);
    });

  // Create New Busing Subscription
  const [createBusingSubscription, { isLoading: isCreating }] =
    useCreateBusingSubscriptionMutation();

  // Update Busing Subscription
  const [updateBusingSubscription, { isLoading: isUpdating }] =
    useUpdateBusingSubscriptionMutation();

  // Upload student list
  const [uploadStudentList, { isLoading: isUploading }] =
    useUploadStudentListMutation();

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "start_time") {
      setFormData((prev) => {
        const next = { ...prev, start_time: value };

        if (next.end_time && toMinutes(next.end_time) <= toMinutes(value)) {
          next.end_time = "";
          setErrors((p) => ({
            ...p,
            end_time: `${t("end")} ${t("time")} ${t("must")} ${t("be")} ${t(
              "after",
            )} ${t("start")} ${t("time")}`,
          }));
        } else {
          setErrors((p) => ({ ...p, end_time: "" }));
        }

        return next;
      });

      setErrors((p) => ({ ...p, start_time: "" }));
      return;
    }

    if (name === "end_time") {
      if (!formData.start_time) {
        setErrors((p) => ({
          ...p,
          end_time: `${t("please")} ${t("select")} ${t("start")} ${t(
            "time",
          )} ${t("first")}`,
        }));
        return;
      }

      if (toMinutes(value) <= toMinutes(formData.start_time)) {
        setErrors((p) => ({
          ...p,
          end_time: `${t("end")} ${t("time")} ${t("must")} ${t("be")} ${t(
            "after",
          )} ${t("start")} ${t("time")}`,
        }));
        return;
      }

      setFormData((prev) => ({ ...prev, end_time: value }));
      setErrors((p) => ({ ...p, end_time: "" }));
      return;
    }

    if (name === "return_end_time") {
      if (!formData.return_start_time) {
        setErrors((p) => ({
          ...p,
          return_end_time: t("errors.select_afternoon_start_first"),
        }));
        return;
      }
      if (toMinutes(value) <= toMinutes(formData.return_start_time)) {
        setErrors((p) => ({
          ...p,
          return_end_time: t("errors.afternoon_end_after_start"),
        }));
        return;
      }
      setFormData((prev) => ({ ...prev, return_end_time: value }));
      setErrors((p) => ({ ...p, return_end_time: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    // Re-check reserved_seats errors when value changes
    if (name === "reserved_seats") {
      const requested = parseInt(value, 10) || 0;
      const vehicleMax = parseInt(selectedVehicle?.seat, 10) || 0;

      // Vehicle capacity check
      if (vehicleMax > 0 && requested > vehicleMax) {
        setErrors((p) => ({
          ...p,
          reserved_seats: t("errors.reserved_exceed_capacity", {
            requested,
            vehicleMax,
          }),
          // `Reserved Seats (${requested}) cannot exceed vehicle capacity (${vehicleMax} seats).`,
        }));
      } else {
        setErrors((p) => ({ ...p, reserved_seats: "" }));
      }

      // CSV limit check
      if (csvRowCount !== null) {
        if (requested > 0 && csvRowCount > requested) {
          setErrors((p) => ({
            ...p,
            student_file: t("errors.csv_more_than_reserved", {
              csvRowCount,
              requested,
            }),

            // `CSV has ${csvRowCount} students but Reserved Seats is ${requested}. Please reduce students or increase Reserved Seats.`,
          }));
        } else {
          setErrors((p) => ({ ...p, student_file: "" }));
        }
      }
    }
  };

  const handleChangeDate = (data, name) => {
    if (!data) {
      setFormData((prev) => ({ ...prev, [name]: null }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: data }));
  };

  const isMorningAfternoon = formData.subscription_type === "morning_afternoon";

  const validateForm = (data) => {
    const newErrors = {};
    const requiredFields = [
      "subscription_type",
      "start_date",
      "end_date",
      "start_time",
      "end_time",
      "reserved_seats",
    ];

    requiredFields.forEach((key) => {
      const value = data[key];
      if (!value || (typeof value === "string" && value.trim() === "")) {
        newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
      }
    });

    if (isMorningAfternoon) {
      if (!data.return_start_time)
        newErrors.return_start_time = `${t("errors.afternoon_start_time")} ${t("errors.required")}`;
      if (!data.return_end_time)
        newErrors.return_end_time = `${t("errors.afternoon_end_time")} ${t("errors.required")}`;
    }

    // Reserved seats must not exceed vehicle's available seats
    if (data.reserved_seats && selectedVehicle?.seat) {
      const requested = parseInt(data.reserved_seats, 10);
      const vehicleMax = parseInt(selectedVehicle.seat, 10);
      if (requested > vehicleMax) {
        newErrors.reserved_seats = t("errors.reserved_exceed_capacity", {
          requested,
          vehicleMax,
        });

        // `Reserved Seats (${requested}) cannot exceed vehicle capacity (${vehicleMax} seats).`;
      }
    }

    // Student file required on create
    if (!selectedReservation && !studentFile) {
      newErrors.student_file = t("errors.student_file_required");
    }

    // CSV student count must not exceed reserved_seats
    if (csvRowCount !== null) {
      const limit = parseInt(data.reserved_seats, 10) || 0;
      if (csvRowCount > limit) {
        newErrors.student_file = t("errors.csv_more_than_reserved", {
          csvRowCount,
          requested: limit,
        });

        // `CSV has ${csvRowCount} students but Reserved Seats is ${limit}. Please reduce students or increase Reserved Seats.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (selectedReservation) {
      setFormData({
        selectedReservation_id: selectedReservation?.id || "",
        start_date: parseLocalDate(selectedReservation.start_date),
        end_date: parseLocalDate(selectedReservation.end_date),
        start_time: selectedReservation?.start_time || "",
        end_time: selectedReservation?.end_time || "",
        return_start_time: selectedReservation?.return_start_time || "",
        return_end_time: selectedReservation?.return_end_time || "",
        reserved_seats: selectedReservation?.reserved_seats || "",
        subscription_type: selectedReservation?.subscription_type || "morning",
        vehicle_id: selectedReservation?.vehicle?.id || "",
        notes: selectedReservation?.notes || "",
      });
    }
  }, [selectedReservation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm(formData)) return;
    toast.dismiss();

    try {
      const payload = {
        ...formData,
        start_date: formatDateToYMD(formData.start_date),
        end_date: formatDateToYMD(formData.end_date),
      };

      let response;
      if (selectedReservation) {
        // Update existing subscription
        response = await updateBusingSubscription({
          id: selectedReservation.id,
          data: payload,
        }).unwrap();
      } else {
        // Create new subscription
        response = await createBusingSubscription(payload).unwrap();
      }

      if (response.statusCode === 200 || response.statusCode === 201) {
        let toastMsg = selectedReservation
          ? "Reservation Updated Successfully"
          : "Reservation Created Successfully";

        // If a student file was selected during create, upload it now
        if (!selectedReservation && studentFile && response.data?.id) {
          try {
            const uploadResult = await uploadStudentList({
              id: response.data.id,
              file: studentFile,
              source: "creation",
            }).unwrap();
            const inserted = uploadResult.data?.inserted ?? 0;
            toastMsg += `. ${inserted} student(s) assigned.`;
          } catch (uploadErr) {
            toast.warning(
              `Reservation created but student upload failed: ${uploadErr?.data?.message || "Unknown error"}`,
            );
          }
        }

        toast.success(toastMsg);
        onClose();
        setFormData(initialState);
        setStudentFile(null);
      }
    } catch (err) {
      const mesg = err?.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        vendor_id: user?.id,
        transfer_to_vendor: 1,
      }));
    }
    if (selectedVehicle) {
      setFormData((prev) => ({
        ...prev,
        vehicle_id: selectedVehicle?.vehicle?.id || selectedVehicle?.id,
      }));
    }
  }, [user, selectedVehicle]);

  return (
    <div
      className="popupBox p-4"
      style={{ maxHeight: "90vh", overflowY: "auto" }}
    >
      <form onSubmit={handleSubmit}>
        <ul className="form p-0 m-0 d-flex flex-wrap">
          {/* Start Date */}
          <li className="col-6">
            <CustomDatePicker
              label={`${t("start")} ${t("date")}`}
              selected={formData.start_date}
              onChange={(date) => handleChangeDate(date, "start_date")}
              minDate={tomorrow}
              error={errors.start_date}
              className="formInput"
            />
          </li>

          {/* End Date */}
          <li className="col-6">
            <CustomDatePicker
              label={`${t("end")} ${t("date")}`}
              selected={formData.end_date}
              onChange={(date) => handleChangeDate(date, "end_date")}
              minDate={formData.start_date || tomorrow}
              error={errors.end_date}
              className="formInput"
            />
          </li>

          {/* Start Time */}
          <li className="col-6">
            <label className="form-label fw-semibold">
              {isMorningAfternoon ? "Morning " : ""}
              {t("start")} {t("time")} <span className="text-danger">*</span>
            </label>
            <input
              type="time"
              className="formInput"
              name="start_time"
              value={formData.start_time || ""}
              onChange={handleChange}
            />
            {errors.start_time && (
              <span className="text-danger small">{errors.start_time}</span>
            )}
          </li>

          {/* End Time */}
          <li className="col-6">
            <label className="form-label fw-semibold">
              {isMorningAfternoon ? "Morning " : ""}
              {t("end")} {t("time")} <span className="text-danger">*</span>
            </label>
            <input
              type="time"
              className="formInput"
              name="end_time"
              value={formData.end_time || ""}
              onChange={handleChange}
              min={formData.start_time || undefined}
            />
            {errors.end_time && (
              <span className="text-danger small">{errors.end_time}</span>
            )}
          </li>

          {/* Afternoon Times — only for morning_afternoon */}
          {isMorningAfternoon && (
            <>
              <li className="col-6">
                <label className="form-label fw-semibold">
                  Afternoon {t("start")} {t("time")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="time"
                  className="formInput"
                  name="return_start_time"
                  value={formData.return_start_time || ""}
                  onChange={handleChange}
                />
                {errors.return_start_time && (
                  <span className="text-danger small">
                    {errors.return_start_time}
                  </span>
                )}
              </li>
              <li className="col-6">
                <label className="form-label fw-semibold">
                  Afternoon {t("end")} {t("time")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <input
                  type="time"
                  className="formInput"
                  name="return_end_time"
                  value={formData.return_end_time || ""}
                  onChange={handleChange}
                />
                {errors.return_end_time && (
                  <span className="text-danger small">
                    {errors.return_end_time}
                  </span>
                )}
              </li>
            </>
          )}

          {/* Subscription Type */}
          <li className="col-12">
            <label className="form-label fw-semibold">
              {t("reservation")} {t("type")}{" "}
              <span className="text-danger">*</span>
            </label>
            <select
              className="formSelect"
              name="subscription_type"
              value={formData.subscription_type}
              onChange={handleChange}
            >
              <option value="morning">{t("morning")}</option>
              <option value="afternoon">{t("afternoon")}</option>
              <option value="morning_afternoon">
                {t("morning")} & {t("afternoon")}
              </option>
            </select>
            {errors.subscription_type && (
              <span className="text-danger small">
                {errors.subscription_type}
              </span>
            )}
          </li>

          {/* Reserved Seats */}
          <li className="col-12">
            <label className="form-label fw-semibold">
              {t("reserved")} {t("seats")}{" "}
              <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              className="formInput"
              name="reserved_seats"
              value={formData.reserved_seats || ""}
              onChange={handleChange}
            />
            {errors.reserved_seats && (
              <span className="text-danger small">{errors.reserved_seats}</span>
            )}
          </li>

          {/* Student List Upload — only on create */}
          {!selectedReservation && (
            <li className="col-12">
              <label className="form-label fw-semibold">
                Student List (CSV){" "}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="form-control formInput h-auto"
                onChange={async (e) => {
                  const file = e.target.files?.[0] || null;
                  setStudentFile(file);
                  setErrors((p) => ({ ...p, student_file: "" }));
                  if (file) {
                    const count = await countCsvRows(file);
                    setCsvRowCount(count);
                    const limit = parseInt(formData.reserved_seats, 10) || 0;
                    if (limit > 0 && count > limit) {
                      setErrors((p) => ({
                        ...p,
                        student_file: `CSV has ${count} students but Reserved Seats is ${limit}. Please reduce students or increase Reserved Seats.`,
                      }));
                    }
                  } else {
                    setCsvRowCount(null);
                  }
                }}
              />
              {studentFile && (
                <small className="text-success">{studentFile.name}</small>
              )}
              {errors.student_file && (
                <span className="text-danger small d-block">
                  {errors.student_file}
                </span>
              )}
              <small className="text-muted d-block mt-1">
                Export from All Students page and upload here to assign students
                automatically.
              </small>
            </li>
          )}

          {/* Notes */}
          <li className="col-12">
            <label className="form-label fw-semibold">{t("Notes")}</label>
            <textarea
              name="notes"
              rows={4}
              className="formInput"
              value={formData.notes || ""}
              onChange={handleChange}
              placeholder={`${t("enter")} ${t("notes")}`}
            />
          </li>

          {/* Buttons */}
          <li className="d-flex col-12">
            <div className="col-6 pe-2 pt-2">
              <Button className="smallBtn full cancel" onClick={onClose}>
                {t("btns.cancel")}
              </Button>
            </div>
            <div className="col-6 ps-2 pt-2">
              <Button
                type="submit"
                className="smallBtn full"
                disabled={isCreating || isUpdating || isUploading}
              >
                {isCreating || isUpdating || isUploading ? (
                  <div className="spinner-border" role="status" />
                ) : (
                  t("book")
                )}
              </Button>
            </div>
          </li>
        </ul>
      </form>
    </div>
  );
};

export default BusingSubscriptions;
